import type { Tweet } from "@podx/core/types";
import type { TokenMention } from "./TokenExtractor";

export interface CryptoSignal {
  token: string;
  signal: 'BUY' | 'SELL' | 'HOLD' | 'WATCH';
  confidence: number;
  reasoning: string[];
  price?: number;
  volume24h?: number;
  socialScore: number;
  sentimentScore: number;
  botAdjustedScore: number;
  accountDiversity: number;
}

export class SignalGenerator {
  generateSignals(tokenMentions: TokenMention[]): CryptoSignal[] {
    const signals: CryptoSignal[] = [];

    for (const mention of tokenMentions) {
      if (mention.count < 3) continue; // Need minimum mentions for signal

      const signal = this.calculateEnhancedSignal(mention);
      if (signal) signals.push(signal);
    }

    return signals.sort((a, b) => b.confidence - a.confidence);
  }

  private calculateEnhancedSignal(mention: TokenMention): CryptoSignal | null {
    const { symbol, count, sentiment, sentimentScore, avgEngagement, tweets, uniqueAccounts, botScore, shillProbability } = mention;
    let confidence = 0;
    const reasoning: string[] = [];

    // Base confidence on mention frequency (weighted by unique accounts)
    const accountDiversity = uniqueAccounts / count; // Higher = more diverse mentions
    const frequencyScore = Math.min(count * 8, 35);
    const diversityBonus = accountDiversity * 15;
    confidence += frequencyScore + diversityBonus;
    reasoning.push(`${count} mentions from ${uniqueAccounts} accounts`);

    // Advanced sentiment analysis
    const sentimentMultiplier = Math.abs(sentimentScore) * 25;
    confidence += sentimentScore > 0 ? sentimentMultiplier : -sentimentMultiplier * 0.5;
    reasoning.push(`Sentiment score: ${sentimentScore.toFixed(2)}`);

    // Bot-adjusted confidence
    const botPenalty = botScore * 40; // Reduce confidence for bot-heavy mentions
    confidence -= botPenalty;
    if (botScore > 0.3) {
      reasoning.push(`Bot influence detected (${(botScore * 100).toFixed(0)}% of accounts)`);
    }

    // Shill probability penalty
    const shillPenalty = shillProbability * 30;
    confidence -= shillPenalty;
    if (shillProbability > 0.4) {
      reasoning.push(`High shill probability (${(shillProbability * 100).toFixed(0)}%)`);
    }

    // Engagement score with bot adjustment
    const engagementScore = Math.min(avgEngagement / 10, 20);
    confidence += engagementScore;
    reasoning.push(`Avg engagement: ${avgEngagement}`);

    // Recent activity bonus
    const recentTweets = tweets.filter(t =>
      new Date(t.createdAt) > new Date(Date.now() - 24 * 60 * 60 * 1000)
    );
    if (recentTweets.length > count * 0.3) { // 30% of mentions are recent
      confidence += 10;
      reasoning.push('Recent activity surge detected');
    }

    // Account diversity bonus
    if (accountDiversity > 0.7) {
      confidence += 10;
      reasoning.push('High account diversity');
    }

    // Determine signal type with enhanced logic
    let signal: 'BUY' | 'SELL' | 'HOLD' | 'WATCH';

    // Apply stricter thresholds for bot/shill filtered signals
    const adjustedConfidence = Math.max(0, Math.min(100, confidence));

    if (adjustedConfidence > 65 && sentimentScore > 0.1 && shillProbability < 0.5) {
      signal = 'BUY';
    } else if (adjustedConfidence < 25 || sentimentScore < -0.2) {
      signal = 'SELL';
    } else if (adjustedConfidence > 40 && sentimentScore > -0.1) {
      signal = 'WATCH';
    } else {
      signal = 'HOLD';
    }

    return {
      token: symbol,
      signal,
      confidence: adjustedConfidence,
      reasoning,
      socialScore: avgEngagement,
      sentimentScore,
      botAdjustedScore: confidence + botPenalty + shillPenalty, // Raw score before penalties
      accountDiversity
    };
  }
}
